import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { capitalize } from "lodash-es";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGetGlobalOffice } from "@/features/office/_api/use-global-office";
import { useAnchorSnapshot } from "@/hooks/use-anchor-snapshot";
import { useOpenSnapshot } from "@/hooks/use-open-snapshot";
import { partitionSelected } from "@/libs/partition-selected";
import type { PurposeOption } from "./purpose-select";

type PurposesSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: PurposeOption[];
  onChange: (value: PurposeOption[]) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

export default function PurposesSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: PurposesSelectProps) {
  const { data: globalValues } = useGetGlobalOffice();
  const purposeOptions = globalValues?.purposes || [];
  const [open, toggleOpen] = useToggle(defaultOpen);

  const sortPurposesAz = useMemo(() => {
    const collator = new Intl.Collator(undefined, {
      usage: "sort",
      sensitivity: "base",
      numeric: true,
    });
    return (a: PurposeOption, b: PurposeOption) =>
      collator.compare(a.title, b.title);
  }, []);

  // Live — the trigger badges summarise the CURRENT selection, and freezing them would also
  // freeze their A-Z order.
  const selectedIds = useMemo(
    () => new Set((value || []).map((v) => v.id)),
    [value],
  );
  const selectedPurposes = useMemo(() => {
    if (!value?.length || !purposeOptions.length) {
      return [];
    }
    return purposeOptions
      .filter((p) => selectedIds.has(p.id))
      .sort(sortPurposesAz);
  }, [value, purposeOptions, selectedIds, sortPurposesAz]);

  // The list, by contrast, groups by the selection the popover OPENED with: regrouping on each
  // tick moves the row across the separator, which remounts it, resets cmdk's highlight to the
  // first row and scrolls the list back to the top. Reopening regroups.
  const selectedIdList = useMemo(() => (value || []).map((v) => v.id), [value]);
  const openedSelected = useOpenSnapshot(selectedIdList, { open });
  const openedIds = useMemo(() => new Set(openedSelected), [openedSelected]);
  const { openedSelectedPurposes, openedUnselectedPurposes } = useMemo(() => {
    const { selected, unselected } = partitionSelected(
      purposeOptions,
      (purpose) => purpose.id,
      openedIds,
    );
    return {
      openedSelectedPurposes: [...selected].sort(sortPurposesAz),
      openedUnselectedPurposes: [...unselected].sort(sortPurposesAz),
    };
  }, [purposeOptions, openedIds, sortPurposesAz]);

  const togglePurpose = (purpose: PurposeOption) => {
    const currentValue = value || [];
    const newValue = currentValue.find((v) => v.id === purpose.id)
      ? currentValue.filter((v) => v.id !== purpose.id)
      : [...currentValue, purpose];
    onChange(newValue);
  };

  // The badges wrap to a second row as purposes are ticked, which grows the trigger downward.
  // Freezing the anchor — never the trigger — keeps the open popover where it opened.
  const anchor = useAnchorSnapshot<HTMLDivElement>(open);

  const handleOpenChange = (open: boolean) => {
    if (disabled) {
      return;
    }
    onOpenChange?.(open);
    toggleOpen(open);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          data-test="purposes-select-trigger"
          role="combobox"
          className={cn(
            /* sorted */
            "relative",
            "flex items-center justify-between",
            "w-full min-h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            className,
          )}
          {...props}
          ref={anchor.triggerRef}
        >
          {value?.length > 0 && purposeOptions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedPurposes.map((purpose) => (
                <Badge key={purpose.id} shape="rounded" size="xs">
                  {capitalize(purpose.title)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-text-dark-min line-clamp-1 text-sm">
              Select purposes
            </p>
          )}

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />

          {anchor.style && (
            <PopoverAnchor asChild>
              <span
                aria-hidden
                className="pointer-events-none"
                style={anchor.style}
              />
            </PopoverAnchor>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 min-w-50">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No purpose found.</CommandEmpty>
            {openedSelectedPurposes.length > 0 && (
              <CommandGroup>
                {openedSelectedPurposes.map((purpose: PurposeOption) => (
                  <CommandItem
                    key={purpose.id}
                    value={purpose.id}
                    keywords={[purpose.title]}
                    className={cn(
                      /* sorted */
                      "group flex items-center gap-2",
                      "text-text-dark-high",
                    )}
                    onSelect={() => togglePurpose(purpose)}
                  >
                    <Checkbox checked={selectedIds.has(purpose.id)} />
                    <span
                      className={cn(
                        /* sorted */
                        "grow",
                        "line-clamp-1 break-all text-sm leading-5 font-normal text-text-dark-max",
                      )}
                    >
                      {capitalize(purpose.title)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {openedSelectedPurposes.length > 0 &&
              openedUnselectedPurposes.length > 0 && <CommandSeparator />}
            {openedUnselectedPurposes.length > 0 && (
              <CommandGroup>
                {openedUnselectedPurposes.map((purpose: PurposeOption) => (
                  <CommandItem
                    key={purpose.id}
                    value={purpose.id}
                    keywords={[purpose.title]}
                    className={cn(
                      /* sorted */
                      "group flex items-center gap-2",
                      "text-text-dark-high",
                    )}
                    onSelect={() => togglePurpose(purpose)}
                  >
                    <Checkbox
                      checked={selectedIds.has(purpose.id)}
                      className={cn(
                        !selectedIds.has(purpose.id) &&
                          "opacity-0 group-data-[selected=true]:opacity-100",
                      )}
                    />
                    <span
                      className={cn(
                        /* sorted */
                        "grow",
                        "line-clamp-1 break-all text-sm leading-5 font-normal text-text-dark-max",
                      )}
                    >
                      {capitalize(purpose.title)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
